import { Minus, Plus } from "lucide-react";
import type { ComponentProps } from "react";
import { useEffect, useRef, useState } from "react";

import { Flex } from "components/core/Flex";

import { Input } from "./Input";
import { StepButton } from "./StepButton";

type Props = Omit<
  ComponentProps<typeof Input>,
  "type" | "value" | "onChange" | "inputMode"
> & {
  value?: number | null;
  onChange?: (value: number | null) => void;
  min?: number;
  max?: number;
  precision?: number;
  step?: number;
  stepLabel?: string;
  stepButtons?: boolean;
};

// Trailing zeros only where they are honest: a value the precision cannot
// express (a bound finer than the step) keeps its own digits rather than
// showing a rounded number the field does not hold.
const format = (value: number | null | undefined, precision?: number) => {
  if (value == null) return "";
  if (precision == null) return String(value);

  const rounded = value.toFixed(precision);

  return Number(rounded) === value ? rounded : String(value);
};

const decimals = (value: number) => String(value).split(".")[1]?.length ?? 0;

const NumberInput = ({
  value,
  onChange,
  min,
  max,
  precision,
  step,
  stepLabel,
  stepButtons = true,
  disabled,
  readOnly,
  onBlur,
  onFocus,
  onKeyDown,
  ...rest
}: Props) => {
  const displayPrecision = precision ?? (step != null ? decimals(step) : undefined);
  const [text, setText] = useState(() => format(value, displayPrecision));
  const focused = useRef(false);
  const lastEmitted = useRef(value);

  useEffect(() => {
    if (!focused.current || value !== lastEmitted.current) {
      setText(format(value, displayPrecision));
    }
  }, [value, displayPrecision]);

  const emit = (next: number | null) => {
    lastEmitted.current = next;
    onChange?.(next);
  };

  const held = () =>
    format(value === undefined ? lastEmitted.current : value, displayPrecision);

  const clamp = (value: number) => {
    let next = precision != null ? Number(value.toFixed(precision)) : value;
    if (min != null) next = Math.max(min, next);
    if (max != null) next = Math.min(max, next);
    return next;
  };

  const commit = (raw: string) => {
    if (raw === "") {
      setText(held());
      return;
    }

    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) {
      setText(held());
      return;
    }

    const next = clamp(parsed);
    emit(next);
    setText(format(next, displayPrecision));
  };

  const typed = Number.isFinite(Number(text)) && text !== "" ? Number(text) : null;
  const current = typed ?? min;

  const stepBy = (direction: 1 | -1) => {
    if (readOnly) return;
    const from = typed ?? 0;
    const by = step ?? 1;
    const next = clamp(
      Number((from + direction * by).toFixed(Math.max(decimals(from), decimals(by))))
    );
    if (next === typed) return;
    emit(next);
    setText(format(next, displayPrecision));
  };

  const field = (
    <Input
      disabled={disabled}
      inputMode={precision === 0 ? "numeric" : "decimal"}
      readOnly={readOnly}
      type="text"
      value={text}
      onBlur={(event) => {
        focused.current = false;
        commit(text);
        onBlur?.(event);
      }}
      onChange={(event) => {
        const raw = event.target.value;
        setText(raw);
        if (raw === "") {
          emit(null);
          return;
        }
        const parsed = Number(raw);
        if (Number.isFinite(parsed)) emit(clamp(parsed));
      }}
      onFocus={(event) => {
        focused.current = true;
        onFocus?.(event);
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter") commit(text);
        if (event.key === "ArrowUp" || event.key === "ArrowDown") {
          event.preventDefault();
          stepBy(event.key === "ArrowUp" ? 1 : -1);
        }
        onKeyDown?.(event);
      }}
      {...rest}
    />
  );

  if (step == null || !stepButtons) {
    return field;
  }

  const name = stepLabel ?? "value";

  return (
    <Flex fullWidth align="center" gap={2}>
      {field}
      <StepButton
        disabled={
          disabled || readOnly || (min != null && current != null && current <= min)
        }
        glyph={Minus}
        label={`Decrease ${name}`}
        onClick={() => stepBy(-1)}
      />
      <StepButton
        disabled={
          disabled || readOnly || (max != null && current != null && current >= max)
        }
        glyph={Plus}
        label={`Increase ${name}`}
        onClick={() => stepBy(1)}
      />
    </Flex>
  );
};

export { NumberInput };
